import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { populateDefaults, populateDemoData } from "@/utils/demo-data";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useQueryClient } from "@tanstack/react-query";

type SetupStep = "defaults" | "suppliers" | "contracts" | "done";

export function EnvironmentSetupScreen() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { activeWorkspaceId, isLoading: workspaceLoading } = useWorkspace();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState<SetupStep>("defaults");
  const setupRef = useRef(false);

  useEffect(() => {
    if (!user || workspaceLoading || !activeWorkspaceId) return;

    const checkAndSetup = async () => {
      // Check if EULA is accepted
      const { data: authData } = await supabase.auth.getUser();
      const eulaAccepted = authData?.user?.user_metadata?.eula_accepted_version;
      if (!eulaAccepted) return;

      // Check if setup already completed
      const { data: prefs } = await supabase
        .from("user_preferences")
        .select("setup_completed")
        .eq("user_id", user.id)
        .maybeSingle();

      if (prefs?.setup_completed === true) return;

      // Need to set up — show screen
      setVisible(true);

      if (setupRef.current) return;
      setupRef.current = true;

      try {
        // Step 1: Populate defaults
        setCurrentStep("defaults");
        await populateDefaults(user.id);

        // Step 2: Create suppliers & contacts
        setCurrentStep("suppliers");
        // Small delay so user sees progress
        await new Promise(r => setTimeout(r, 400));

        // Step 3: Create contracts
        setCurrentStep("contracts");
        await populateDemoData(user.id);

        // Step 4: Mark setup complete and demo data active
        setCurrentStep("done");
        await supabase
          .from("user_preferences")
          .upsert({ user_id: user.id, setup_completed: true, has_demo_data: true } as any, { onConflict: "user_id" });

        // Prevent stale empty-state cache on contracts page
        await queryClient.invalidateQueries({ queryKey: ["contracts"] });
        await queryClient.invalidateQueries({ queryKey: ["suppliers"] });
        await queryClient.invalidateQueries({ queryKey: ["contacts"] });

        // Brief pause to show completion
        await new Promise(r => setTimeout(r, 800));

        setVisible(false);
        navigate("/contracts");
      } catch (err) {
        console.error("Error during environment setup:", err);
        // Even on error, proceed so user isn't stuck
        setVisible(false);
        navigate("/contracts");
      }
    };

    checkAndSetup();

    // Also listen for EULA acceptance
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "USER_UPDATED") {
        checkAndSetup();
      }
    });

    return () => subscription.unsubscribe();
  }, [user, navigate, workspaceLoading, activeWorkspaceId, queryClient]);

  if (!visible) return null;

  const steps: { key: SetupStep; label: string }[] = [
    { key: "defaults", label: t("setup.stepDefaults", "Setting up currencies & categories...") },
    { key: "suppliers", label: t("setup.stepSuppliers", "Creating demo vendors & contacts...") },
    { key: "contracts", label: t("setup.stepContracts", "Generating sample contracts...") },
    { key: "done", label: t("setup.stepDone", "All done!") },
  ];

  const currentIdx = steps.findIndex(s => s.key === currentStep);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="flex flex-col items-center gap-8 max-w-md text-center px-6">
        {/* Logo / Title */}
        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            {t("setup.title", "Setting up your environment")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("setup.subtitle", "We're preparing demo data so you can explore the platform right away.")}
          </p>
        </div>

        {/* Steps */}
        <div className="w-full space-y-3">
          {steps.map((step, idx) => {
            const isActive = idx === currentIdx;
            const isComplete = idx < currentIdx;

            return (
              <div
                key={step.key}
                className={cn(
                  "flex items-center gap-3 rounded-lg px-4 py-3 transition-all duration-300",
                  isActive && "bg-primary/10 border border-primary/20",
                  isComplete && "opacity-60",
                  !isActive && !isComplete && "opacity-30"
                )}
              >
                {isComplete ? (
                  <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />
                ) : isActive ? (
                  <Loader2 className="h-5 w-5 text-primary shrink-0 animate-spin" />
                ) : (
                  <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30 shrink-0" />
                )}
                <span
                  className={cn(
                    "text-sm text-left",
                    isActive && "text-foreground font-medium",
                    isComplete && "text-muted-foreground",
                    !isActive && !isComplete && "text-muted-foreground"
                  )}
                >
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Progress bar */}
        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500 ease-out"
            style={{ width: `${((currentIdx + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
